/**
 * Canonical entropy heat map hierarchy: Business → 5 subdomains → factors.
 * Used to map API entropy (scope = factor or domain) into a consistent tree
 * and to show rollup color at collapsed level.
 */

/** Top-level subdomains under Business (display order). */
export const ENTROPY_SUBDOMAINS = [
  'Finance',
  'Operations',
  'Strategy',
  'Revenue',
  'Resources',
] as const;

export type EntropySubdomain = (typeof ENTROPY_SUBDOMAINS)[number];

/** Factor code → human-readable label. */
export const ENTROPY_FACTOR_LABELS: Record<string, string> = {
  FD: 'Financial Discipline',
  PSE: 'Process Efficiency',
  RS: 'Resource Sufficiency',
  CVI: 'Customer Value',
  DES: 'Demand Efficiency',
  PM: 'Product Maturity',
  PRA: 'Priority Alignment',
  QFD: 'Quality Failure Discipline',
  DLE: 'Delivery Efficiency',
  MFQ: 'MarketFit Quality',
  PPE: 'Pricing and Packaging Efficiency',
  SFE: 'Sales Funnel Efficiency',
  GSD: 'Growth Spend Discipline',
};

/**
 * Factor code → subdomain(s) it belongs to.
 * A factor may appear under multiple subdomains; for rollup we use all.
 */
export const ENTROPY_FACTOR_TO_SUBDOMAINS: Record<string, EntropySubdomain[]> = {
  FD: ['Finance'],
  PSE: ['Operations'],
  RS: ['Resources'],
  CVI: ['Revenue'],
  DES: ['Revenue'],
  PM: ['Revenue', 'Operations'],
  PRA: ['Revenue', 'Operations', 'Strategy'],
  QFD: ['Operations'],
  DLE: ['Revenue'],
  MFQ: ['Revenue'],
  PPE: ['Revenue'],
  SFE: ['Revenue'],
  GSD: ['Finance', 'Strategy'],
};

/** API may return "Resource"; we normalize to "Resources" for display. */
export const DOMAIN_DISPLAY_ALIAS: Record<string, EntropySubdomain> = {
  Resource: 'Resources',
  Resources: 'Resources',
  Finance: 'Finance',
  Operations: 'Operations',
  Strategy: 'Strategy',
  Revenue: 'Revenue',
};

/** All factor codes we know (for iteration). */
export const ENTROPY_FACTOR_IDS = Object.keys(ENTROPY_FACTOR_LABELS);

/** REM (Revenue Efficiency Metric) factors: GEOMEAN(DES, PPE, DLE, PM, PSE, RS, PRA). CVI is DES component. */
export const REM_FACTORS = ['DES', 'CVI', 'PPE', 'DLE', 'PM', 'PSE', 'RS', 'PRA'];

/** EEM (Expense Efficiency Metric) factors: GEOMEAN(PSE, RS, PRA, FD, QFD, GSD). */
export const EEM_FACTORS = ['PSE', 'RS', 'PRA', 'FD', 'QFD', 'GSD'];
